import {first} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Inject,
  Input,
} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SmdDataTable} from './datatable.component';
import {SmdDataTableColumn} from './datatable-column';
import {DatatableMultivalueDialogComponent} from './datatable-multivalue-dialog.component';
import {ResourceService} from '../../../resource/resource.service';
import {AddViewEvent} from '../../../resource/events/addviewevent';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

export class SmdDataRowModel {
  constructor(
    public model: any,
    public checked?: boolean,
    public mark?: SmdDataRowMark,
    public icon?: SmdDataRowIcon,
  ) {}
}

export enum SmdDataRowMark {
  Selected,
  Add,
  Remove,
  HideRow,
}

export class SmdDataRowIcon {
  constructor(
    public icon: string,
    public styleClass?: string,
  ) {}
}

@Component({
  selector: '[smd-datatable-row]',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (renderCheckbox) {
      <td class="smd-datatable-body-checkbox">
        <div class="smat-checkbox">
          <mat-checkbox [(ngModel)]="row.checked" (change)="_parent._onRowCheckChange(row)">
          </mat-checkbox>
        </div>
      </td>
    }
    @if (_parent.headerRowIcon && row.icon) {
      <td class="smd-row-icon {{ row.icon.styleClass }}">
        <mat-icon (click)="_onIconClick()">{{ row.icon.icon }}</mat-icon>
      </td>
    }
    @if (_parent.headerRowIcon && !row.icon) {
      <td></td>
    }
    @for (column of columns; track column) {
      <td
        [class.smd-numeric-column]="column?.numeric"
        (click)="_onClick(column, row.model, $event)"
      >
        <span class="smd-column-title">
          {{ column?.title }}
        </span>
        <span class="smd-cell-data">
          @switch (column?.dataTypeCombined) {
            @case ('Timeline') {
              <a><language key="DatatableOpenTimeline" defaultValue="Open Timeline"></language></a>
            }
            @case ('Reference') {
              <a
                matTooltipShowDelay="1000"
                matTooltip="{{ (column?.getResource(row.model) | async)?.DisplayName }}"
              >
                {{ (column?.getResource(row.model) | async)?.DisplayName }}
              </a>
            }
            @case ('DetailLink') {
              <a
                matTooltipShowDelay="1000"
                matTooltip="{{
                  column?.getFieldValue(
                    row.model,
                    _parent.getCaughtObservable(column?.languageResource) | async,
                    'No ' + column?.field + ' defined.'
                  )
                }}"
                matTooltipPosition="right"
              >
                {{
                  column?.getFieldValue(
                    row.model,
                    _parent.getCaughtObservable(column?.languageResource) | async
                  )
                }}
              </a>
            }
            @case ('MultiValued') {
              <span class="multivalue-container">
                @switch (column?.dataType) {
                  @case ('Reference') {
                    <a class="multivalue-content">
                      {{ (column?.getFirstResource(row.model) | async)?.DisplayName }}</a
                    >
                  }
                  @case ('DateTime') {
                    <span class="multivalue-content">{{
                      _parent.getCaughtObservable(column?.getFirstFieldValue(row.model) | localDate)
                        | async
                    }}</span>
                  }
                  @default {
                    <span class="multivalue-content">{{
                      column?.getFirstFieldValue(row.model)
                    }}</span>
                  }
                }
                <span class="multivalue-count">{{
                  column?.getFieldValue(
                    row.model,
                    _parent.getCaughtObservable(column?.languageResource) | async
                  )
                }}</span>
              </span>
            }
            @case ('DateTime') {
              <span
                matTooltipShowDelay="1000"
                matTooltip="{{
                  _parent.getCaughtObservable(
                    column?.getFieldValue(
                      row.model,
                      _parent.getCaughtObservable(column?.languageResource) | async
                    ) | localDate
                  ) | async
                }}"
              >
                {{
                  _parent.getCaughtObservable(
                    column?.getFieldValue(
                      row.model,
                      _parent.getCaughtObservable(column?.languageResource) | async
                    ) | localDate
                  ) | async
                }}
              </span>
            }
            @default {
              <span
                matTooltipShowDelay="1000"
                matTooltip="{{
                  column?.getFieldValue(
                    row.model,
                    _parent.getCaughtObservable(column?.languageResource) | async
                  )
                }}"
              >
                {{
                  column?.getFieldValue(
                    row.model,
                    _parent.getCaughtObservable(column?.languageResource) | async
                  )
                }}
              </span>
            }
          }
        </span>
      </td>
    }
  `,
  standalone: false,
})
export class SmdDataTableRowComponent {
  @Input() row: SmdDataRowModel;
  @Input() renderCheckbox: boolean;
  @Input() columns: SmdDataTableColumn[];

  constructor(
    @Inject(forwardRef(() => SmdDataTable)) public _parent: SmdDataTable,
    private dialog: MatDialog,
    public changeDetector: ChangeDetectorRef,
    private resourceService: ResourceService,
    private envConfig: EnvironmentConfigService,
  ) {}

  _onClick(column: SmdDataTableColumn, model: any, event: MouseEvent) {
    if (column == null || model == null) return;
    if (
      column.multiValued &&
      Array.isArray(model[column.field]) &&
      model[column.field].length == 1
    ) {
      if (model[column.field].length == 1) {
        // opens dialog for click in multivalue column
        let dialogRef: MatDialogRef<DatatableMultivalueDialogComponent> = this.dialog.open(
          DatatableMultivalueDialogComponent,
        );
        dialogRef.componentInstance.displayName = model.DisplayName;
        dialogRef.componentInstance.attributeName = column.title;
        if (column.dataType === 'Reference') {
          dialogRef.componentInstance.observable = this.resourceService.searchResources(
            '/' +
              this.envConfig.getObjectTypeName('Resource') +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              '=/' +
              model[this.envConfig.getAttributeName('ObjectType')] +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              "='" +
              model.ObjectID +
              "']/" +
              column.field +
              ']',
            [
              this.envConfig.getAttributeName('ObjectID'),
              this.envConfig.getAttributeName('DisplayName'),
              this.envConfig.getAttributeName('Description'),
            ].join(','),
            99999,
          );
        } else {
          dialogRef.componentInstance.model = model[column.field];
        }
        dialogRef.componentInstance.dataType = column.dataType;
        this._parent
          .getCaughtObservable(dialogRef.componentInstance.openDetailEmitter)
          .subscribe((val: {objectID: string; event: MouseEvent}) => {
            if (val.event.ctrlKey || val.event.button === 1) {
              window.open('#/get/' + val.objectID);
            } else {
              this._parent.onAddView.emit(new AddViewEvent('get', val.objectID));
            }
          });
      } else if (column.dataType === 'Reference') {
        this._parent.onAddView.emit(new AddViewEvent('get', model[column.field][0]));
      }
    }
    if (
      column.multiValued &&
      Array.isArray(model[column.field]) &&
      model[column.field].length > 1
    ) {
      if (model[column.field].length > 1) {
        // opens dialog for click in multivalue column
        let dialogRef: MatDialogRef<DatatableMultivalueDialogComponent> = this.dialog.open(
          DatatableMultivalueDialogComponent,
        );
        dialogRef.componentInstance.displayName = model.DisplayName;
        dialogRef.componentInstance.attributeName = column.title;
        if (column.dataType === 'Reference') {
          dialogRef.componentInstance.observable = this.resourceService.searchResources(
            '/' +
              this.envConfig.getObjectTypeName('Resource') +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              '=/' +
              model[this.envConfig.getAttributeName('ObjectType')] +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              "='" +
              model.ObjectID +
              "']/" +
              column.field +
              ']',
            [
              this.envConfig.getAttributeName('ObjectID'),
              this.envConfig.getAttributeName('DisplayName'),
              this.envConfig.getAttributeName('Description'),
            ].join(','),
            99999,
          );
        } else {
          dialogRef.componentInstance.model = model[column.field];
        }
        dialogRef.componentInstance.dataType = column.dataType;
        this._parent
          .getCaughtObservable(dialogRef.componentInstance.openDetailEmitter)
          .subscribe((val: {objectID: string; event: MouseEvent}) => {
            if (val.event.ctrlKey || val.event.button === 1) {
              window.open('#/get/' + val.objectID);
            } else {
              this._parent.onAddView.emit(new AddViewEvent('get', val.objectID));
            }
          });
      } else if (column.dataType === 'Reference') {
        this._parent.onAddView.emit(new AddViewEvent('get', model[column.field][0]));
      }
    } else if (this._parent.disableLinks) {
      this._parent._onRowIconClick(this.row);
    } else if (column.field === this.envConfig.getAttributeName('Timeline')) {
      if (
        model[this.envConfig.getAttributeName('ObjectType')] ===
        this.envConfig.getObjectTypeName('Request')
      ) {
        if (
          model[this.envConfig.getAttributeName('Target')] != null &&
          ResourceService.guidRegex.test(model[this.envConfig.getAttributeName('Target')])
        ) {
          if (event.ctrlKey || event.button === 1) {
            window.open('#/timeline/' + model[this.envConfig.getAttributeName('Target')]);
          } else {
            this._parent.onAddView.emit(
              new AddViewEvent('timeline', model[this.envConfig.getAttributeName('Target')]),
            );
          }
        } else {
          this._parent
            .getCaughtObservable(
              this.resourceService.getResource(model[this.envConfig.getAttributeName('ObjectID')]),
            )
            .pipe(first())
            .subscribe((res: any) => {
              if (
                res[this.envConfig.getAttributeName('Target')] != null &&
                ResourceService.guidRegex.test(res[this.envConfig.getAttributeName('Target')])
              ) {
                if (event.ctrlKey || event.button === 1) {
                  window.open('#/timeline/' + res[this.envConfig.getAttributeName('Target')]);
                } else {
                  this._parent.onAddView.emit(
                    new AddViewEvent('timeline', res[this.envConfig.getAttributeName('Target')]),
                  );
                }
              } else {
                alert('Sorry, Target not found.');
              }
            });
        }
      } else {
        if (event.ctrlKey || event.button === 1) {
          window.open('#/timeline/' + model[this.envConfig.getAttributeName('ObjectID')]);
        } else {
          this._parent.onAddView.emit(
            new AddViewEvent('timeline', model[this.envConfig.getAttributeName('ObjectID')]),
          );
        }
      }
    } else if (ResourceService.guidRegex.test(model[column.field])) {
      if (event.ctrlKey || event.button === 1) {
        window.open('#/get/' + model[column.field]);
      } else {
        this._parent.onAddView.emit(new AddViewEvent('get', model[column.field]));
      }
    } else if (column.detailLink) {
      if (event.ctrlKey || event.button === 1) {
        window.open('#/get/' + model[this.envConfig.getAttributeName('ObjectID')]);
      } else {
        this._parent.onAddView.emit(
          new AddViewEvent('get', model[this.envConfig.getAttributeName('ObjectID')]),
        );
      }
    }
  }

  _onIconClick(): void {
    this._parent._onRowIconClick(this.row);
  }

  @HostBinding('class.mark-selected')
  get markSelected(): boolean {
    return this.row.mark != null && this.row.mark === SmdDataRowMark.Selected;
  }

  @HostBinding('class.mark-add')
  get markAdd(): boolean {
    return this.row.mark != null && this.row.mark === SmdDataRowMark.Add;
  }

  @HostBinding('class.mark-remove')
  get markRemove(): boolean {
    return this.row.mark != null && this.row.mark === SmdDataRowMark.Remove;
  }
}
